import { useMemo, useState } from 'react';
import { Check, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useIsMobile } from '@/hooks/use-mobile';

import AiIcon from '/ai-svg-oreog.svg';

interface AIAgentSelectorProps {
  selectedAgent: AIAgent | null;
  onAgentChange: (agent: AIAgent | null) => void;
  isDarkMode: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

import { type AIAgent, ALL_AGENTS } from '@/types/aiAgents';

const availableAgents: AIAgent[] = ALL_AGENTS;

export const AIAgentSelector = ({
  selectedAgent,
  onAgentChange,
  isDarkMode,
  isOpen,
  onOpenChange,
  hideTrigger,
  isListening = false,
}: AIAgentSelectorProps & { isListening?: boolean }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const isMobile = useIsMobile();

  const open = useMemo(() => {
    return typeof isOpen === 'boolean' ? isOpen : internalOpen;
  }, [isOpen, internalOpen]);

  const setIsOpen = (open: boolean) => {
    if (onOpenChange) onOpenChange(open);
    else setInternalOpen(open);
  };

  const filteredAgents = availableAgents.filter((agent) =>
    agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedAgents = filteredAgents.reduce((acc, agent) => {
    if (!acc[agent.category]) {
      acc[agent.category] = [];
    }
    acc[agent.category].push(agent);
    return acc;
  }, {} as Record<string, AIAgent[]>);

  const handleAgentSelect = (agent: AIAgent) => {
    onAgentChange(selectedAgent?.id === agent.id ? null : agent);
    setIsOpen(false);
  };

  return (
    <>
      {!hideTrigger && (
        <div className="relative group">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsOpen(true)}
            className={`p-0 h-8 backdrop-blur-sm ${isMobile ? 'w-10 rounded-full flex items-center justify-center' : 'rounded-full px-3 hidden xs:flex'} ${isDarkMode
                ? 'bg-[#2e2e2e]/80 text-white-400 hover:text-white hover:bg-gray-700/50'
                : 'bg-gray-200/80 text-black-600 hover:text-gray-900 hover:bg-gray-300'
              } ${isListening ? 'hidden' : ''}`}
            aria-label="AI Agents"
          >
            {selectedAgent ? (
              <>
                <img
                  src={selectedAgent.icon}
                  alt={selectedAgent.name}
                  className={`${isMobile ? 'w-5 h-5' : 'w-5 h-5 mr-2'} rounded-full bg-white border border-gray-300 dark:border-gray-600 object-contain`}
                />
                {!isMobile && (
                  <span className="truncate max-w-[120px]">
                    {selectedAgent.name}
                  </span>
                )}
              </>
            ) : (
              <>
                <img
                  src={AiIcon}
                  alt="AI"
                  className={`${isMobile ? 'w-5 h-5' : 'w-5 h-5 mr-1'}`}
                />
                {!isMobile && <span>Agents</span>}
              </>
            )}
            <div className="hidden xs:block absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              {selectedAgent ? 'Change Agent' : 'Select Agent'}
            </div>
          </Button>
        </div>
      )}

      {open && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center px-3 sm:px-4 ${isDarkMode ? 'bg-black/60' : 'bg-black/40'
            }`}
          onClick={() => setIsOpen(false)}
        >
          <div
            className={`sm:max-w-2xl w-full max-w-[96vw] max-h-[85vh] rounded-[20px] border overflow-hidden shadow-lg transform-gpu transition-all duration-200 ${isDarkMode
                ? 'bg-[#303030] border-[#1c1c1c] text-white'
                : 'bg-white border-gray-200 text-gray-900'
              }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 pt-4 pb-2 border-b border-white/5">
              <h2 className="text-base font-semibold">Select AI Agent</h2>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className={`p-1 rounded-full transition-colors ${isDarkMode
                    ? 'hover:bg-white/10 text-gray-300'
                    : 'hover:bg-gray-100 text-gray-600'
                  }`}
                aria-label="Close agent selector"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="relative">
                <Search
                  className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}
                />
                <Input
                  placeholder="Search agents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`pl-10 border-none outline-none focus:outline-none focus:ring-0 ring-0 ${isDarkMode
                      ? 'bg-[#252525] border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
              </div>

              <div className="max-h-96 overflow-y-auto space-y-4 pr-1">
                {Object.entries(groupedAgents).map(([category, agents]) => (
                  <div key={category}>
                    <h3
                      className={`text-sm text-left font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}
                    >
                      {category}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {agents.map((agent) => {
                        const isSelected = selectedAgent?.id === agent.id;

                        return (
                          <Card
                            key={agent.id}
                            className={`p-3 cursor-pointer transition-colors ${isSelected
                                ? isDarkMode
                                  ? 'bg-blue-600/20 border-blue-600'
                                  : 'bg-blue-50 border-blue-300'
                                : isDarkMode
                                  ? 'bg-[#252525] border-[#292929] hover:border-[#2c2c2c] hover:bg-[#2c2c2c]'
                                  : 'bg-white border-gray-200 hover:bg-gray-50'
                              }`}
                            onClick={() => handleAgentSelect(agent)}
                          >
                            <div className="flex items-center gap-3">
                              <img
                                src={agent.icon}
                                alt={agent.name}
                                className="w-7 h-7 rounded-full bg-white border border-gray-300 dark:border-gray-600 object-contain"
                              />
                              <div className="flex-1 min-w-0">
                                <div
                                  className={`font-medium text-left truncate ${isDarkMode ? 'text-white' : 'text-gray-900'
                                    }`}
                                >
                                  {agent.name}
                                </div>
                                <div
                                  className={`text-xs text-left truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                    }`}
                                >
                                  {agent.description}
                                </div>
                              </div>
                              {isSelected && (
                                <div
                                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-500 dark:bg-blue-400"
                                >
                                  <Check
                                    className="w-3 h-3 text-white dark:text-black"
                                    strokeWidth={3}
                                  />
                                </div>
                              )}

                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export { availableAgents };